/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 * Written by Hendrik, December 28, 2025.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './dashboard.css';
import RenewButton from './RenewButton'
import TextType from './TextType';
import { Border } from 'docx';
import { useMemo } from 'react';

// SVG Icons components
const UsersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const ChartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"></line>
        <line x1="12" y1="20" x2="12" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="14"></line>
    </svg>
);

const PendingIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
);


export default function Dashboard() {
    const [stats, setStats] = useState({ total_students: 0, average_mark: 0, pending_marking: 0 });
    const [user, setUser] = useState({ id: null, expiryDate: null });
    const [loading, setLoading] = useState(true);
    const [upcomingEvent, setUpcomingEvent] = useState(null);
    const [greeting, setGreeting] = useState('');

    useEffect(() => {
        const loadDashboard = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_dashboard.php`);
                setStats(response.data.stats || { total_students: 0, average_mark: 0, pending_marking: 0 });

                let username = localStorage.getItem('username');
                if (!username || username === 'undefined' || username === 'null') {
                    username = undefined;
                }
                if (response.data.user_info) {
                    setUser({
                        id: response.data.user_info.id,
                        expiryDate: response.data.user_info.expiry_date
                    });
                    localStorage.setItem('expiry_date', response.data.user_info.expiry_date);
                    // If username is not set, try to get from user_info
                    if (!username && response.data.user_info.username) {
                        let backendUsername = response.data.user_info.username;
                        if (
                            backendUsername !== undefined &&
                            backendUsername !== null &&
                            backendUsername !== 'undefined' &&
                            backendUsername !== 'null'
                        ) {
                            username = backendUsername;
                            localStorage.setItem('username', username);
                        }
                    }
                }
                // Final sanitize
                if (!username || username === 'undefined' || username === 'null') {
                    username = 'User';
                }
                
                const hour = new Date().getHours();
                let greet = hour < 12 ? 'Good morning' : (hour < 17 ? 'Good afternoon' : 'Good evening');
                // Remove any accidental 'undefined' or 'null' in the greeting string
                let finalGreeting = `${greet}, ${username}!`;
                finalGreeting = finalGreeting.replace(/undefined|null/gi, '').replace(/,\s*!/, '!').replace(/\s{2,}/g, ' ').trim();
                setGreeting(finalGreeting);
            } catch (error) {
                console.error("Dashboard load failed", error);
                // fallback greeting
                const hour = new Date().getHours();
                let greet = hour < 12 ? 'Good morning' : (hour < 17 ? 'Good afternoon' : 'Good evening');
                setGreeting(`${greet}, User!`);
            } finally {
                setLoading(false);
            }
        };
        loadDashboard();
    }, []);

    // Helper functions
    const today = new Date().toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    // --- LOCK LOGIC ---
    const isExpired = useMemo(() => {
        if (!user.expiryDate) return false;
        return new Date(user.expiryDate) < new Date();
    }, [user.expiryDate]);

    const formattedExpiry = useMemo(() => {
        return user.expiryDate ? new Date(user.expiryDate).toLocaleDateString() : '';
    }, [user.expiryDate]);
    const quotes = [
        "Education is the most powerful weapon which you can use to change the world. - Nelson Mandela",
        "The future belongs to those who believe in the beauty of their dreams. - Eleanor Roosevelt",
        "The only way to do great work is to love what you do. - Steve Jobs",
        "Success is not final, failure is not fatal: It is the courage to continue that counts. - Winston Churchill",
        "Believe you can and you're halfway there. - Theodore Roosevelt",
        "The best way to predict the future is to create it. - Peter Drucker",
        "Don't watch the clock; do what it does. Keep going. - Sam Levenson",
        "Education is not preparation for life; education is life itself. - John Dewey",
        "Every student can learn, just not on the same day, or the same way. - George Evans",
        "The beautiful thing about learning is that no one can take it away from you. - B.B. King",
        "The expert in anything was once a beginner. - Helen Hayes",
        "It always seems impossible until it's done. - Nelson Mandela",
        "The mind is not a vessel to be filled but a fire to be kindled. - Plutarch",
        "Education is the key to unlocking the world, a passport to freedom. - Oprah Winfrey",
        "The only limit to our realization of tomorrow will be our doubts of today. - Franklin D. Roosevelt"
        
    ];
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    //Get the current time and display it on the dashboard, updating every minute
    const [currentTime, setCurrentTime] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);


    return (
        <div className='dash_main'>
            <header className="dash_header">
                <div>
                    <h1>
                    <TextType text={[greeting]} 
                    typingSpeed={75}
                    pauseDuration={1500}
                    showCursor
                    cursorCharacter="_"
                    deletingSpeed={100}
                    variableSpeedEnabled={false}
                    variableSpeedMin={60}
                    variableSpeedMax={120}
                    cursorBlinkDuration={0.5}
                    />
                    </h1>
                    <p className="date_text">{today}  -  {currentTime.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}</p>
                    <p className="date_text" style={{marginTop: '10px',fontSize:'20px'}}>{randomQuote}</p>
                </div>
            </header>

            {/* --- CONDITION: If Expired, SHOW LOCKED SCREEN ONLY --- */}
            {isExpired ? (
                <div className="locked-container" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '60vh',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    margin: '20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ padding: '40px', maxWidth: '500px' }}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" style={{ marginBottom: '20px' }}>
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>

                        <h2 style={{ color: '#c62828', marginBottom: '10px' }}>Access Locked</h2>
                        <p style={{ color: '#666', marginBottom: '30px', fontSize: '1.1em' }}>
                            Your subscription expired on <strong>{formattedExpiry}</strong>.<br />
                            To access your dashboard and student data, please renew your account.
                        </p>

                        <RenewButton user={user} />

                        <p style={{ marginTop: '20px', fontSize: '0.9em', color: '#999' }}>
                            Secure payment via Yoco
                        </p>
                    </div>
                </div>
            ) : (
                /* --- IF ACTIVE: SHOW NORMAL DASHBOARD --- */
                loading ? (
                    <div className="loading_state">Loading statistics...</div>
                ) : (
                    <div className='card_container'>
                        <div className='stat_card'>
                            <div className='icon_wrapper blue'><UsersIcon /></div>
                            <div className='card_content'>
                                <h3>Total Students</h3>
                                <h4 className="stat_number">{stats.total_students}</h4>
                                <p className="stat_desc">Enrolled learners</p>
                            </div>
                        </div>

                        <div className='stat_card'>
                            <div className='icon_wrapper green'><ChartIcon /></div>
                            <div className='card_content'>
                                <h3>Overall Average</h3>
                                <h4 className="stat_number">{stats.average_marks}%</h4>
                                <p className="stat_desc">Across all subjects</p>
                            </div>
                        </div>

                        <div className='stat_card'>
                            <div className='icon_wrapper orange'><PendingIcon /></div>
                            <div className='card_content'>
                                <h3>Pending Marking</h3>
                                <h4 className="stat_number">{stats.pending_marking}</h4>
                                <p className="stat_desc">Incomplete records</p>
                            </div>
                        </div>
                    </div>
                )
            )}
        </div>
    );
}